// App.tsx
import React from 'react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ExternalLink, Github, ArrowLeft, CheckCircle2 } from "lucide-react";
import { Link } from "react-router-dom";
import Project3 from "@/assets/achivements/Project3.jpg";

const SmartIrrigationSystem: React.FC = () => {
  return (
    <main className="min-h-screen bg-background">
      {/* Back Button */}
      <div className="w-full border-b border-border">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="max-w-6xl mx-auto px-4 pt-18 md:pt-20">
            <Link to="/" className="flex items-center text-muted-foreground hover:text-primary transition-colors">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Portfolio
            </Link>
          </div>
        </div>
      </div>

      {/* Project Content */}
      <section className="py-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Project Header */}
          <div className="mb-8">
            <div className="flex items-center gap-4 mb-4">
              <Badge>Featured Project</Badge>
              <Badge variant="outline" className="bg-green-500/10 text-green-500 border-green-500/20">
                Active
              </Badge>
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold mb-4 text-foreground">
              Smart Irrigation System using Precision Farming
            </h1>
            <div className="flex flex-wrap gap-3 mb-6">
              <Button asChild variant="default" className="bg-gradient-to-r from-primary to-purple-500 hover:opacity-90 border-none">
                <a href="https://github.com/Mohan-Balaji/Smart-Irrigation-System-using-Precision-Farming" target="_blank" rel="noopener noreferrer">
                  <Github className="mr-2 h-4 w-4" />
                  View Source
                </a>
              </Button>
              <Button asChild variant="outline">
                <a href="https://youtu.be/r6CCejWSwXM" target="_blank" rel="noopener noreferrer">
                  <ExternalLink className="mr-2 h-4 w-4" />
                  Project Demo
                </a>
              </Button>
            </div>
          </div>

          {/* Project Video/Image */}
          <div className="mb-12">
            <img 
              src={Project3}
              alt="Smart Irrigation System"
              className="rounded-lg shadow-sm w-full h-auto"
            />
          </div>

          {/* Project Description */}
          <div className="prose prose-invert max-w-none mb-12">
            <h2 className="text-2xl font-bold mb-4">About the Project</h2>
            <p className="text-muted-foreground mb-6">
              A sophisticated irrigation system that optimizes water usage in agriculture by monitoring soil moisture 
              levels and controlling irrigation accordingly. This project combines IoT technology with precision farming 
              techniques to create a sustainable and efficient agricultural solution.
            </p>
            <p className="text-muted-foreground mb-6">
              The system addresses the critical challenge of water conservation in agriculture while ensuring optimal 
              crop growth conditions. By implementing smart sensors and automated control systems, it significantly 
              reduces water wastage and improves crop yield.
            </p>

            {/* Project Details Card */}
            <Card className="p-6 bg-white/5 border-border mb-8">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                <div>
                  <h3 className="text-sm font-medium text-muted-foreground mb-1">Role</h3>
                  <p className="font-medium">Lead Developer & System Architect</p>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-muted-foreground mb-1">Duration</h3>
                  <p className="font-medium">5 months</p>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-muted-foreground mb-1">Impact</h3>
                  <p className="font-medium text-primary">60% Water Savings</p>
                </div>
              </div>
            </Card>

            {/* Key Features */}
            <h2 className="text-2xl font-bold mb-4 mt-8">Key Features</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
              <div className="flex items-start gap-3">
                <CheckCircle2 className="h-5 w-5 text-primary mt-1 flex-shrink-0" />
                <div>
                  <h4 className="font-semibold">Precision Watering</h4>
                  <p className="text-sm text-muted-foreground">Automated irrigation based on real-time soil moisture data</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle2 className="h-5 w-5 text-primary mt-1 flex-shrink-0" />
                <div>
                  <h4 className="font-semibold">Weather Integration</h4>
                  <p className="text-sm text-muted-foreground">Smart scheduling based on weather forecasts</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle2 className="h-5 w-5 text-primary mt-1 flex-shrink-0" />
                <div>
                  <h4 className="font-semibold">Remote Monitoring</h4>
                  <p className="text-sm text-muted-foreground">Real-time system status and analytics dashboard</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle2 className="h-5 w-5 text-primary mt-1 flex-shrink-0" />
                <div>
                  <h4 className="font-semibold">Crop-Specific Profiles</h4>
                  <p className="text-sm text-muted-foreground">Customized watering schedules for different crops</p>
                </div>
              </div>
            </div>

            {/* Technical Details */}
            <h2 className="text-2xl font-bold mb-4 mt-8">Technical Details</h2>
            <div className="mb-6">
              <h3 className="text-xl font-semibold mb-3">Technologies Used</h3>
              <div className="flex flex-wrap gap-2 mb-6">
                <Badge variant="outline">IoT</Badge>
                <Badge variant="outline">Soil Moisture Sensors</Badge>
                <Badge variant="outline">Arduino</Badge>
                <Badge variant="outline">Automated Watering</Badge>
                <Badge variant="outline">Cloud Computing</Badge>
                <Badge variant="outline">Data Analytics</Badge>
                <Badge variant="outline">Mobile App</Badge>
              </div>
            </div>

            <div className="mb-6">
              <h3 className="text-xl font-semibold mb-3">System Architecture</h3>
              <ul className="list-disc list-inside text-muted-foreground space-y-2">
                <li>Distributed sensor network for soil monitoring</li>
                <li>Centralized control system with Arduino</li>
                <li>Cloud-based data storage and analytics</li>
                <li>Mobile application for remote control</li>
                <li>Automated valve control system</li>
              </ul>
            </div>

            <div className="mb-6">
              <h3 className="text-xl font-semibold mb-3">Project Impact</h3>
              <Card className="p-6 bg-primary/5 border-primary/10">
                <p className="text-muted-foreground">
                  The system has demonstrated a 60% reduction in water usage while maintaining optimal crop growth 
                  conditions. This significant water savings, combined with improved crop yields, makes it an 
                  essential tool for sustainable agriculture in water-scarce regions.
                </p>
              </Card>
            </div>
          </div>

          {/* Project Screenshots */}
          <div className="space-y-8">
            <h2 className="text-2xl font-bold mb-4">Project Gallery</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <img
                  src={Project3}
                  alt="System Implementation"
                  className="rounded-lg shadow-sm w-full h-auto"
                />
                <p className="text-sm text-muted-foreground mt-2">
                  Complete system implementation showing sensor network and control unit
                </p>
              </div>
              <div>
                <img
                  src={Project3}
                  alt="Mobile Dashboard"
                  className="rounded-lg shadow-sm w-full h-auto"
                />
                <p className="text-sm text-muted-foreground mt-2">
                  Mobile application dashboard for system monitoring and control
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default SmartIrrigationSystem;
